/**
 * =============================================================================
 * Componente: AffiliateKpiReferralsModal
 * Versão: 1.0.0
 * Release: 2026-08-17 — Drill-down de auditoria nos KPIs do painel do afiliado
 *
 * Descrição:
 *   Modal que lista as indicações que compõem o KPI clicado na home do
 *   afiliado (card "Progresso do período" e funil 3D).
 *
 *   - Fonte de dados: hook existente `useAffiliateReferrals({ startDate,
 *     endDate })`, que consome a RPC `get_affiliate_referrals_page`
 *     (e-mail/telefone já chegam mascarados do servidor). Nenhuma RPC nova.
 *   - Filtro por métrica é aplicado no cliente sobre o mesmo conjunto usado
 *     pelos KPIs, garantindo consistência com os números exibidos.
 *   - Layout: tabela no desktop e cards no mobile, no mesmo padrão visual
 *     da página `/afiliado/indicacoes` (tokens `aff-*`).
 * =============================================================================
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAffiliateReferrals,
  type AffiliateReferralRow,
  type ReferralStatus,
} from '@/hooks/useAffiliateReferrals';
import { formatCurrency } from '@/lib/currency-utils';
import { cn } from '@/lib/utils';

/** Métricas suportadas pelo drill-down. */
export type AffiliateKpiMetric = 'referrals' | 'conversions' | 'earned';

interface AffiliateKpiReferralsModalProps {
  /** Métrica clicada; `null` mantém o modal fechado. */
  metric: AffiliateKpiMetric | null;
  onOpenChange: (open: boolean) => void;
  /** Mesmo período usado pelos KPIs da home. */
  periodParams: { startDate?: Date; endDate?: Date };
  /** Rótulo curto do período (ex.: "Agosto" ou "01/08 – 17/08"). */
  periodLabel?: string;
}

/** Metadados de exibição por métrica. */
const METRIC_META: Record<
  AffiliateKpiMetric,
  { title: string; description: string }
> = {
  referrals: {
    title: 'Indicações',
    description:
      'Leads criados no período selecionado, na ordem mais recente primeiro.',
  },
  conversions: {
    title: 'Conversões',
    description:
      'Indicações do período que já foram convertidas em negócio fechado.',
  },
  earned: {
    title: 'Aprovado',
    description:
      'Indicações do período que geraram comissão. O KPI soma pela data de aprovação da comissão, então pode divergir quando a aprovação ocorre em outro período.',
  },
};

/** Status → label + cor do dot (mesmo padrão da página de Indicações). */
const STATUS_META: Record<ReferralStatus, { label: string; dotClass: string }> = {
  pending: { label: 'Pendente', dotClass: 'bg-[hsl(var(--aff-warning))]' },
  qualified: { label: 'Qualificado', dotClass: 'bg-[hsl(var(--aff-accent-2))]' },
  converted: { label: 'Convertido', dotClass: 'bg-[hsl(var(--aff-success))]' },
  lost: { label: 'Perdido', dotClass: 'bg-[hsl(var(--aff-danger))]' },
  cancelled: { label: 'Cancelada', dotClass: 'bg-[hsl(var(--aff-danger))]' },
};

function StatusBadge({ status }: { status: ReferralStatus }) {
  const meta =
    STATUS_META[status] ?? {
      label: status,
      dotClass: 'bg-[hsl(var(--aff-muted-foreground))]',
    };
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--aff-foreground))]">
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', meta.dotClass)} />
      {meta.label}
    </span>
  );
}

/** Formata data com hora: dd/MM/yyyy HH:mm */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Aplica o filtro correspondente à métrica clicada. */
function filterByMetric(
  rows: AffiliateReferralRow[],
  metric: AffiliateKpiMetric,
): AffiliateReferralRow[] {
  if (metric === 'conversions') {
    return rows.filter((r) => r.status === 'converted');
  }
  if (metric === 'earned') {
    return rows.filter((r) => Number(r.commission_value ?? 0) > 0);
  }
  return rows;
}

export function AffiliateKpiReferralsModal({
  metric,
  onOpenChange,
  periodParams,
  periodLabel,
}: AffiliateKpiReferralsModalProps) {
  const open = metric !== null;

  // Reaproveita o hook/RPC já usado na listagem de indicações.
  // `enabled` implícito: só busca quando o afiliado está pronto; o modal
  // fechado apenas não renderiza o conteúdo.
  const { data, isLoading, isError, refetch } = useAffiliateReferrals(periodParams);

  const rows = useMemo(
    () => (metric ? filterByMetric(data ?? [], metric) : []),
    [data, metric],
  );

  const meta = metric ? METRIC_META[metric] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'affiliate-shell p-0 gap-0 overflow-hidden',
          // Mobile: tela cheia. Desktop: modal amplo com altura controlada.
          'max-w-full h-[100dvh] rounded-none',
          'sm:max-w-4xl sm:h-auto sm:max-h-[85vh] sm:rounded-2xl',
          'flex flex-col bg-[hsl(var(--aff-bg))]',
        )}
      >
        <DialogHeader className="px-5 py-4 border-b border-[hsl(var(--aff-border))] text-left shrink-0">
          <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold text-[hsl(var(--aff-foreground))]">
            <Users className="h-4 w-4 text-[hsl(var(--aff-primary))]" />
            {meta?.title ?? 'Indicações'}
            {periodLabel && (
              <span className="text-[hsl(var(--aff-muted-foreground))] font-normal">
                · {periodLabel}
              </span>
            )}
            {!isLoading && !isError && (
              <span className="ml-1 rounded-full bg-[hsl(var(--aff-surface-2))] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--aff-muted-foreground))]">
                {rows.length}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[hsl(var(--aff-muted-foreground))]">
            {meta?.description}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 md:p-5">
            {isLoading && (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-xl" />
                ))}
              </div>
            )}

            {isError && !isLoading && (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                  Não foi possível carregar as indicações.
                </p>
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  Tentar novamente
                </Button>
              </div>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-sm font-semibold text-[hsl(var(--aff-foreground))]">
                  Nenhum registro neste indicador
                </p>
                <p className="mt-1 text-[12px] text-[hsl(var(--aff-muted-foreground))]">
                  Ajuste o período no filtro do painel para ver outros resultados.
                </p>
              </div>
            )}

            {!isLoading && !isError && rows.length > 0 && (
              <>
                {/* Desktop: tabela */}
                <div className="hidden md:block overflow-hidden rounded-xl border border-[hsl(var(--aff-border))]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[hsl(var(--aff-border))] hover:bg-transparent">
                        <TableHead>Lead</TableHead>
                        <TableHead>Campanha</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Criado em</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.id}
                          className="border-[hsl(var(--aff-border))] hover:bg-[hsl(var(--aff-surface-2))]"
                        >
                          <TableCell>
                            <div className="font-semibold text-[hsl(var(--aff-foreground))]">
                              {r.lead_name || 'Lead sem nome'}
                            </div>
                            <div className="text-xs text-[hsl(var(--aff-muted-foreground))]">
                              {r.lead_email || r.lead_phone || '—'}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                            {r.campaign?.name || '—'}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={r.status} />
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums text-[hsl(var(--aff-foreground))]">
                            {Number(r.commission_value ?? 0) > 0
                              ? formatCurrency(Number(r.commission_value))
                              : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                            {formatDate(r.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile: cards */}
                <ul className="grid gap-3 md:hidden">
                  {rows.map((r) => (
                    <li
                      key={r.id}
                      className="rounded-xl border border-[hsl(var(--aff-border))] bg-[hsl(var(--aff-surface))] p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-[13.5px] font-semibold text-[hsl(var(--aff-foreground))]">
                            {r.lead_name || 'Lead sem nome'}
                          </p>
                          <p className="truncate text-[11.5px] text-[hsl(var(--aff-muted-foreground))]">
                            {r.lead_email || r.lead_phone || '—'}
                          </p>
                        </div>
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11.5px] text-[hsl(var(--aff-muted-foreground))]">
                        <span className="truncate">{r.campaign?.name || 'Sem campanha'}</span>
                        <span className="shrink-0">{formatDate(r.created_at)}</span>
                      </div>
                      {Number(r.commission_value ?? 0) > 0 && (
                        <p className="mt-1 text-[13px] font-semibold tabular-nums text-[hsl(var(--aff-foreground))]">
                          {formatCurrency(Number(r.commission_value))}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Rodapé: atalho para a listagem completa */}
        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--aff-border))] px-5 py-3 shrink-0">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-[12px] text-[hsl(var(--aff-primary))] hover:bg-[hsl(var(--aff-primary-soft))] hover:text-[hsl(var(--aff-primary))]"
            onClick={() => onOpenChange(false)}
          >
            <Link to="/afiliado/indicacoes">
              Ver todas <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
